import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaChevronDown, FaArrowRight } from 'react-icons/fa';
import { SOLUTIONS } from '../../data/solutionsMenu';

const PublicNavbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [solutionsHover, setSolutionsHover] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const location = useLocation();
  const isLoggedIn = localStorage.getItem('token');

  // `badge` adds a small chip after the label — used to surface recently
  // shipped surface areas (AI Session Notes + AI Agent on Features, the
  // v1.8.0 release on What's new). Drop the badge once the feature is
  // no longer "new" (4-6 weeks after ship).
  const links = [
    { to: '/features', label: 'Features', badge: 'NEW' },
    { to: '/pricing', label: 'Pricing' },
    { to: '/changelog', label: "What's new" },
    { to: '/api', label: 'Developers' },
  ];

  const isOnSolutionsPage = location.pathname.startsWith('/for/');

  return (
    <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.svg" alt="Appozy" className="w-8 h-8" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="text-xl font-extrabold text-gray-900 tracking-tight">Appozy</span>
          </Link>

          {/* Desktop Links */}
          <div className="hidden md:flex items-center space-x-7">
            {/* Solutions mega-menu — hover-driven, Notion/Linear style */}
            <div
              className="relative"
              onMouseEnter={() => setSolutionsHover(true)}
              onMouseLeave={() => setSolutionsHover(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  isOnSolutionsPage || solutionsHover
                    ? 'text-violet-600'
                    : 'text-gray-600 hover:text-violet-600'
                }`}
              >
                Solutions
                <FaChevronDown className={`text-[9px] transition-transform ${solutionsHover ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown panel — premium two-column grid, opens on hover.
                  The `pt-3` adds invisible padding above the panel so the cursor
                  can travel from the button to the panel without the hover
                  state flickering. */}
              {solutionsHover && (
                // Anchored to the button's left edge so the dropdown opens
                // directly below "Solutions" and grows to the right. The
                // earlier `left-1/2 -translate-x-1/2` was centring on the
                // wrapper (button's own width) which pushed the 640px panel
                // off to the left.
                <div className="absolute top-full left-0 pt-3 z-50">
                  <div className="w-[min(640px,calc(100vw-2rem))] bg-white rounded-2xl shadow-2xl shadow-gray-900/10 border border-gray-100 p-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="px-4 pt-3 pb-2">
                      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400">
                        Solutions by role
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                      {SOLUTIONS.map((s) => {
                        const Icon = s.icon;
                        return (
                          <Link
                            key={s.to}
                            to={s.to}
                            className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                          >
                            <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${s.iconBg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                              <Icon className="text-white text-xs" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1">
                                <p className="text-sm font-bold text-gray-900 group-hover:text-violet-700 transition-colors">
                                  {s.label}
                                </p>
                                <FaArrowRight className="text-[9px] text-gray-300 group-hover:text-violet-500 group-hover:translate-x-0.5 transition-all" />
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                                {s.blurb}
                              </p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    {/* Footer hint inside the panel */}
                    <div className="border-t border-gray-50 px-4 py-3 bg-gray-50/40">
                      <Link
                        to="/features"
                        className="text-xs font-semibold text-violet-600 hover:text-violet-700 inline-flex items-center gap-1"
                      >
                        See all features <FaArrowRight className="text-[9px]" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {links.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? 'text-violet-600'
                    : 'text-gray-600 hover:text-violet-600'
                }`}
              >
                <span>{link.label}</span>
                {link.badge && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-extrabold tracking-wider bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
                    {link.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                className="px-5 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-violet-600 transition-colors">
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow-md transition-all"
                >
                  Get Started Free
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="space-y-1">
              {/* Mobile Solutions — expandable inline */}
              <button
                type="button"
                onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
                aria-expanded={mobileSolutionsOpen}
                className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isOnSolutionsPage
                    ? 'bg-violet-50 text-violet-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>Solutions</span>
                <FaChevronDown className={`text-[10px] transition-transform ${mobileSolutionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileSolutionsOpen && (
                <div className="ml-3 pl-3 border-l border-violet-100 space-y-1 mt-1 mb-2">
                  {SOLUTIONS.map((s) => {
                    const Icon = s.icon;
                    return (
                      <Link
                        key={s.to}
                        to={s.to}
                        onClick={() => { setMobileOpen(false); setMobileSolutionsOpen(false); }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          location.pathname === s.to
                            ? 'bg-violet-50 text-violet-700'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${s.iconBg} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="text-white text-[10px]" />
                        </div>
                        {s.label}
                      </Link>
                    );
                  })}
                </div>
              )}
              {links.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className={`block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.to
                      ? 'bg-violet-50 text-violet-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 space-y-2 px-4">
              {isLoggedIn ? (
                <Link to="/dashboard" onClick={() => setMobileOpen(false)}
                  className="block w-full text-center px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-bold">
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)}
                    className="block w-full text-center px-5 py-2.5 border-2 border-gray-200 text-gray-700 rounded-xl text-sm font-bold">
                    Sign In
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)}
                    className="block w-full text-center px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-bold">
                    Get Started Free
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicNavbar;
